// Persistência da avaliação do usuário (NPS) no localStorage.
//
// Regra: a barra aparece na hora no primeiro acesso. Depois que o usuário vota
// ou fecha, gravamos o timestamp e só voltamos a exibir quando 24h tiverem
// passado — comparação feita contra o relógio do sistema no momento do mount.

export const USER_RATING_STORAGE_KEY = "embarca-log.user_rating";

/** Intervalo mínimo entre duas exibições da barra. */
export const USER_RATING_COOLDOWN_MS = 24 * 60 * 60 * 1000;

export type UserRatingStatus = "dismissed" | "rated";

export interface UserRatingRecord {
  status: UserRatingStatus;
  /** Nota de 1 a 10 — null quando o usuário apenas fechou a barra. */
  score: number | null;
  /** Justificativa da nota — só pedimos para notas baixas. */
  comment: string | null;
  /** Date.now() do momento em que a barra foi fechada ou votada. */
  timestamp: number;
}

export const readUserRatingRecord = (): UserRatingRecord | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(USER_RATING_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<UserRatingRecord>;
    if (typeof parsed?.timestamp !== "number") return null;
    if (parsed.status !== "dismissed" && parsed.status !== "rated") return null;

    return {
      status: parsed.status,
      score: typeof parsed.score === "number" ? parsed.score : null,
      comment: typeof parsed.comment === "string" ? parsed.comment : null,
      timestamp: parsed.timestamp,
    };
  } catch {
    // JSON corrompido ou storage bloqueado (modo privado) — trata como sem registro.
    return null;
  }
};

// Devolve o registro gravado para que o chamador mande o mesmo objeto ao
// backend — storage e API sempre recebem exatamente o mesmo conteúdo.
export const saveUserRatingRecord = (
  status: UserRatingStatus,
  score: number | null = null,
  comment: string | null = null
): UserRatingRecord => {
  const record: UserRatingRecord = {
    status,
    score,
    comment,
    timestamp: Date.now(),
  };

  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(USER_RATING_STORAGE_KEY, JSON.stringify(record));
    } catch {
      // Storage indisponível: a barra some nesta sessão e volta na próxima.
    }
  }

  return record;
};

/**
 * true no primeiro acesso (sem registro) e sempre que 24h tiverem passado
 * desde o último voto/fechamento. Registro inválido ou com timestamp no futuro
 * (relógio alterado) também libera a exibição.
 */
export const shouldShowUserRating = (now: number = Date.now()): boolean => {
  const record = readUserRatingRecord();
  if (!record) return true;
  if (!Number.isFinite(record.timestamp)) return true;

  const elapsed = now - record.timestamp;
  if (elapsed < 0) return true;

  return elapsed >= USER_RATING_COOLDOWN_MS;
};

// --- Gatilhos de exibição -------------------------------------------------
//
// A barra não aparece em qualquer acesso: só no fim do fluxo de pagamento e,
// para quem nunca respondeu nem fechou a barra, no detalhe do pedido.

/**
 * true depois de o usuário ter votado ou fechado a barra ao menos uma vez.
 * Registro corrompido conta como nunca interagido — na dúvida, perguntamos.
 */
export const hasInteractedWithUserRating = (): boolean =>
  readUserRatingRecord() !== null;
